import type { CharacterActionKey } from "@/character/actions";
import { defaultGridSpec, normalizeGridSpec, type CharacterGridSpec } from "@/character/grid";

const STORAGE_PREFIX = "mind-survival/character/v2";

export interface CharacterProfile {
  version: 2;
  name: string;
  createdAt: number;
  spritesheetDataUrl: string;
  grid: CharacterGridSpec;
  actions: Record<CharacterActionKey, string>;
}

const LEGACY_PREFIX = "mind-survival/character/v1";

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

function legacyStorageKey(userId: string): string {
  return `${LEGACY_PREFIX}:${userId}`;
}

type LegacyProfile = {
  name: string;
  createdAt: number;
  actions: Record<string, string>;
  spritesheetDataUrl: string;
};

export function loadCharacterProfile(userId: string): CharacterProfile | null {
  const raw = localStorage.getItem(storageKey(userId));
  if (raw) {
    try {
      const profile = JSON.parse(raw) as CharacterProfile;
      return { ...profile, grid: normalizeGridSpec(profile.grid) };
    } catch {
      localStorage.removeItem(storageKey(userId));
    }
  }

  const legacyRaw = localStorage.getItem(legacyStorageKey(userId));
  if (!legacyRaw) return null;
  try {
    const legacy = JSON.parse(legacyRaw) as LegacyProfile;
    if (!legacy.spritesheetDataUrl || !legacy.actions?.idle) {
      return null;
    }
    const grid = {
      ...defaultGridSpec(),
      regionX: 0,
      regionY: 0,
      sheetColumns: 6,
      alignX: 0,
      alignY: 0,
    };
    return {
      version: 2,
      name: legacy.name,
      createdAt: legacy.createdAt,
      spritesheetDataUrl: legacy.spritesheetDataUrl,
      grid,
      actions: {
        idle: legacy.actions.idle,
        run: legacy.actions.run ?? legacy.actions.idle,
        sit: legacy.actions.sit ?? legacy.actions.work ?? legacy.actions.idle,
        attack: legacy.actions.attack ?? legacy.actions.happy ?? legacy.actions.idle,
      },
    };
  } catch {
    localStorage.removeItem(legacyStorageKey(userId));
    return null;
  }
}

export function saveCharacterProfile(userId: string, profile: CharacterProfile): void {
  localStorage.setItem(storageKey(userId), JSON.stringify(profile));
  import("@/cloud/player-cloud-scheduler").then((m) =>
    m.schedulePlayerCloudPush(userId, "character"),
  );
}

export function deleteCharacterProfile(userId: string): void {
  localStorage.removeItem(storageKey(userId));
  localStorage.removeItem(legacyStorageKey(userId));
}
